// Display formatting for eligibility results.
//
// Separated from the component so the one rule that actually matters here can be
// tested directly:
//
//   A value the payer did not send is UNKNOWN, never zero.
//
// "$0 copay" and "we don't know your copay" look similar on a screen and are
// completely different to a patient standing at the front desk being quoted a
// price. Everything below preserves that distinction.

import { CoverageStatus, EligibilityCheck, NormalizedBenefits } from '../types/eligibility';

export const UNKNOWN = 'Unknown';

/** Money, or "Unknown" when the payer stayed silent. $0 is a real answer. */
export function formatMoney(value?: number): string {
  if (value === undefined || value === null || Number.isNaN(value)) return UNKNOWN;
  return `$${value.toLocaleString('en-US', {
    minimumFractionDigits: value % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
}

export function formatPercent(value?: number): string {
  if (value === undefined || value === null || Number.isNaN(value)) return UNKNOWN;
  return `${value}%`;
}

export function formatCount(value?: number): string {
  if (value === undefined || value === null || Number.isNaN(value)) return UNKNOWN;
  return String(value);
}

/** Tri-state flags: null means the payer said nothing, which is not "No". */
export function formatFlag(value?: boolean | null, yes = 'Yes', no = 'No'): string {
  if (value === true) return yes;
  if (value === false) return no;
  return UNKNOWN;
}

/** "3 of 20 remaining", degrading gracefully when either half is missing. */
export function formatRemainingOfTotal(remaining?: number, total?: number): string {
  if (remaining === undefined && total === undefined) return UNKNOWN;
  if (remaining !== undefined && total !== undefined) return `${remaining} of ${total} remaining`;
  if (remaining !== undefined) return `${remaining} remaining`;
  return `${total} total`;
}

export function formatDeductible(benefits: NormalizedBenefits): string {
  const { deductibleRemaining, deductibleTotal } = benefits;
  if (deductibleRemaining === undefined && deductibleTotal === undefined) return UNKNOWN;
  if (deductibleRemaining !== undefined && deductibleTotal !== undefined) {
    return `${formatMoney(deductibleRemaining)} of ${formatMoney(deductibleTotal)} remaining`;
  }
  if (deductibleRemaining !== undefined) return `${formatMoney(deductibleRemaining)} remaining`;
  return `${formatMoney(deductibleTotal)} total`;
}

/** X12 YYYYMMDD → a readable date. Returns undefined for anything else. */
export function formatX12Date(value?: string): string | undefined {
  if (!value || !/^\d{8}$/.test(value)) return value || undefined;
  const date = new Date(
    Number(value.slice(0, 4)),
    Number(value.slice(4, 6)) - 1,
    Number(value.slice(6, 8))
  );
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export interface StatusPresentation {
  label: string;
  background: string;
  color: string;
}

export function statusPresentation(status: CoverageStatus): StatusPresentation {
  switch (status) {
    case 'active':
      return { label: 'Active Coverage', background: '#E8F5E9', color: '#2E7D32' };
    case 'inactive':
      return { label: 'Not Active', background: '#FFEBEE', color: '#C62828' };
    case 'error':
      return { label: 'Check Failed', background: '#FFF3E0', color: '#E65100' };
    default:
      return { label: 'Unknown', background: '#ECEFF1', color: '#455A64' };
  }
}

/** "just now" / "3 hours ago" / "Sep 1, 2026" for the check timestamp. */
export function formatCheckedAt(checkedAt: Date, now: Date = new Date()): string {
  const minutes = Math.floor((now.getTime() - checkedAt.getTime()) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;

  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;

  return checkedAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Whether a check is old enough that it should be re-run before an appointment.
 * Coverage changes without warning; a two-week-old "active" is not reassurance.
 */
export function isStale(check: Pick<EligibilityCheck, 'expiresAt'>, now: Date = new Date()): boolean {
  return check.expiresAt.getTime() <= now.getTime();
}

/**
 * The single most important line on the card.
 *
 * A carve-out we detected but could not follow is worse than no carve-out at
 * all: the benefits shown belong to the medical plan, not the behavioral
 * administrator who will actually adjudicate the claim.
 */
export function carveOutMessage(check: EligibilityCheck): {
  tone: 'info' | 'warning';
  text: string;
} | null {
  const { carveOut } = check;
  if (!carveOut.detected) return null;

  const who = carveOut.entityName || 'another administrator';

  if (carveOut.secondPassError) {
    return {
      tone: 'warning',
      text: `Behavioral benefits are administered by ${who}, but we could not retrieve them (${carveOut.secondPassError}). The amounts below are the medical plan's and may not apply. Call ${carveOut.phone || who} to confirm.`,
    };
  }

  if (!carveOut.secondPassPerformed) {
    return {
      tone: 'warning',
      text: `Behavioral benefits are administered by ${who}. The amounts below are the medical plan's and may not apply.`,
    };
  }

  return {
    tone: 'info',
    text: `Behavioral benefits are administered by ${who}. The amounts below come from ${who}, not the medical plan.`,
  };
}
